import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, Trash2, ListTree } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { apiClient } from '../../lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export interface StoreMenu {
  id: string;
  title: string;
  handle: string;
  _count?: { items: number };
}

export default function Navigation() {
  const navigate = useNavigate();
  
  const queryClient = useQueryClient();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newHandle, setNewHandle] = useState('');

  const { data: menus, isLoading } = useQuery({
    queryKey: ['store-menus'],
    queryFn: async () => {
      const res: any = await apiClient.get<{ data: StoreMenu[] }>('/merchant/menus');
      return res;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: { title: string; handle: string }) => {
      const res: any = await apiClient.post('/merchant/menus', data);
      return res;
    },
    onSuccess: (data) => {
      toast('Menu created successfully');
      queryClient.invalidateQueries({ queryKey: ['store-menus'] });
      setIsCreateOpen(false);
      navigate(`/store/navigation/${data.id}`);
    },
    onError: () => {
      toast('Failed to create menu');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/merchant/menus/${id}`);
    },
    onSuccess: () => {
      toast('Menu deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['store-menus'] });
    },
    onError: () => {
      toast('Failed to delete menu');
    },
  });

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setNewTitle(val);
    setNewHandle(val.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''));
  };

  if (isLoading) {
    return <div className="flex justify-center p-12">Loading navigation...</div>;
  }

  return (
    <div className="flex-1 space-y-4 p-8 pt-6">
      <div className="flex items-center justify-between">
        <PageHeader 
          title="Navigation" 
          description="Manage the menus and links on your online store."
        />
        <Button onClick={() => setIsCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Menu
        </Button>
      </div>

      <div className="rounded-md border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Handle</TableHead>
              <TableHead>Items</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!menus?.length ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                  No menus found. Create your main menu to get started.
                </TableCell>
              </TableRow>
            ) : (
              menus.map((menu: StoreMenu) => (
                <TableRow key={menu.id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <ListTree className="h-4 w-4 text-muted-foreground" />
                      {menu.title}
                    </div>
                  </TableCell>
                  <TableCell>
                    <code className="rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm">
                      {menu.handle}
                    </code>
                  </TableCell>
                  <TableCell>
                    {menu._count?.items || 0} links
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => navigate(`/store/navigation/${menu.id}`)}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        if (confirm(`Are you sure you want to delete ${menu.title}?`)) {
                          deleteMutation.mutate(menu.id);
                        }
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Menu</DialogTitle>
            <DialogDescription>
              Create a new menu to display on your store's header or footer.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                placeholder="e.g. Main Menu"
                value={newTitle}
                onChange={handleTitleChange}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="handle">Handle</Label>
              <Input
                id="handle"
                value={newHandle}
                onChange={(e) => setNewHandle(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">Used by your theme to load this menu (e.g. linklists.main-menu).</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
            <Button
              onClick={() => createMutation.mutate({ title: newTitle, handle: newHandle })}
              disabled={createMutation.isPending || !newTitle || !newHandle}
            >
              {createMutation.isPending ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
